import { getData, postData, putData } from './FetchNodeServices';

export interface CostingSettings {
  id?: string;
  overheadMethod: 'PERCENTAGE' | 'FIXED' | 'MACHINE_HOURLY';
  overheadPercentage: number;
  labourBasis: 'HOURLY_RATES' | 'PIECE_RATE' | 'BOTH';
  includeRejectionCost: boolean;
  includeReworkCost: boolean;
  costingMethod: 'STANDARD' | 'ACTUAL' | 'BOTH';
  updatedAt?: string;
}

export interface ProductionCostRecord {
  id: string;
  productionOrderId: string;
  productionOrderNumber: string;
  productId: string;
  productName: string;
  variantName: string;
  producedQty: number;
  unit: string;
  costingMethod: 'STANDARD' | 'ACTUAL';
  materialCost: number;
  labourCost: number;
  overheadCost: number;
  rejectionCost: number;
  totalCost: number;
  costPerUnit: number;
  salePrice: number;
  marginAmount?: number;
  marginPct: number;
  totalVariance?: number;
  variancePct?: number;
  varianceStatus?: 'OVER_BUDGET' | 'ON_BUDGET' | 'UNDER_BUDGET';
  grade: 'A' | 'B' | 'C' | 'D';
  status: 'DRAFT' | 'CALCULATED' | 'FINALIZED' | 'LOCKED';
  calculatedAt?: string;
  createdAt?: string;
}

export interface VarianceAnalysisRow {
  poNumber: string;
  productName: string;
  variantName: string;
  qty: number;
  unit: string;
  stdMaterial: number;
  actMaterial: number;
  matVar: number;
  stdLabour: number;
  actLabour: number;
  labVar: number;
  stdOverhead: number;
  actOverhead: number;
  ohVar: number;
  stdRejection: number;
  actRejection: number;
  rejVar: number;
  stdTotal: number;
  actTotal: number;
  totalVar: number;
  varPct: number;
  productionOrderId?: string;
  salePrice?: number;
  status?: string;
  standardCostId?: string | null;
  actualCostId?: string;
}

export interface OrderCostElement {
  type: 'MATERIAL' | 'LABOUR' | 'OVERHEAD';
  description: string;
  unit: string;
  rate: number;
  stdQty: number;
  actQty: number;
  stdAmount: number;
  actAmount: number;
  sourceType: string;
}

export interface OrderCostBreakdown {
  productionOrderId: string;
  poNumber: string;
  productName: string;
  productCode: string;
  status: string;
  standard: { material: number; labour: number; overhead: number; total: number };
  actual: { material: number; labour: number; overhead: number; total: number };
  rejectionShare: number;
  elements: OrderCostElement[];
  overheadPct: number;
  plannedQty: number;
  producedQty: number;
  rejectedQty: number;
  salePrice: number;
  unit: string;
  varianceAmount: number;
  variancePercent: number;
  grade: string | null;
  // True until the order is completed: actual cost and variance are not reported before that
  actualPending?: boolean;
}

export interface CostElementItem {
  id: string;
  productionCostId: string;
  type: 'MATERIAL' | 'LABOUR' | 'OVERHEAD' | 'REJECTION';
  description: string;
  qty: number;
  unit: string;
  rate: number;
  amount: number;
  stdAmount?: number;
  actAmount?: number;
  variance?: number;
  sourceType: string;
}

export const getCostingSettings = async (): Promise<{ success: boolean; data: CostingSettings }> => {
  return await getData('api/v1/manufacturing/costing/settings');
};

export const updateCostingSettings = async (settings: CostingSettings): Promise<{ success: boolean; message: string; data: CostingSettings }> => {
  return await putData('api/v1/manufacturing/costing/settings', settings);
};

export const getStandardCosts = async (
  search: string = '',
  fy: string = '',
  warehouseId?: string | null
): Promise<{ success: boolean; data: ProductionCostRecord[]; summary: any }> => {
  const params: Record<string, any> = { search, fy };
  if (warehouseId) params.warehouseId = warehouseId;
  return await getData('api/v1/manufacturing/costing/standard', params);
};

export const getActualCosts = async (
  search: string = '',
  statusFilter: string = 'all',
  warehouseId?: string | null
): Promise<{ success: boolean; data: ProductionCostRecord[]; summary: any }> => {
  const params: Record<string, any> = { search, statusFilter };
  if (warehouseId) params.warehouseId = warehouseId;
  return await getData('api/v1/manufacturing/costing/actual', params);
};

export const getVarianceAnalysis = async (
  warehouseId?: string | null
): Promise<{ success: boolean; data: VarianceAnalysisRow[] }> => {
  const params: Record<string, any> = {};
  if (warehouseId) params.warehouseId = warehouseId;
  return await getData('api/v1/manufacturing/costing/variance', params);
};

export const getCostingDashboardStats = async (
  warehouseId?: string | null
): Promise<{ success: boolean; data: any }> => {
  const params: Record<string, any> = {};
  if (warehouseId) params.warehouseId = warehouseId;
  return await getData('api/v1/manufacturing/costing/dashboard-stats', params);
};

export const getCostElements = async (id: string): Promise<{ success: boolean; data: { costRecord: ProductionCostRecord; elements: CostElementItem[] } }> => {
  return await getData(`api/v1/manufacturing/costing/elements/${id}`);
};

export const calculateProductionOrderCost = async (payload: {
  productionOrderId: string;
  poNumber?: string;
  productName?: string;
  quantity?: number;
  unit?: string;
  costingMethod: 'STANDARD' | 'ACTUAL';
  status?: string;
  warehouseId?: string | null;
}): Promise<{ success: boolean; message: string; data: ProductionCostRecord }> => {
  return await postData('api/v1/manufacturing/costing/calculate', payload);
};

export const recalculateAllCosts = async (): Promise<{ success: boolean; message: string; data: { updated: number; skipped: string[] } }> => {
  return await postData('api/v1/manufacturing/costing/recalculate-all', {});
};

// Live standard vs actual breakdown of one production order (`ref` = order id or PO number)
export const getOrderCostBreakdown = async (ref: string): Promise<{ success: boolean; message?: string; data: OrderCostBreakdown }> => {
  return await getData(`api/v1/manufacturing/costing/order-breakdown/${encodeURIComponent(ref)}`);
};
